import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Grid,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DownloadIcon from "@mui/icons-material/Download";
import { useDispatch, useSelector } from "react-redux";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { fetchEmpDiscripency } from "../../actions/EmpDiscripencyAction";
import homeImage from "../../assets/DTS2.png";

// ------------ Styles ------------
const PageContainer = styled(Box)(() => ({
  maxWidth: "900px",
  margin: "0 auto",
  padding: "10px 15px",
  fontFamily: "'Poppins', sans-serif",
}));

const StickyCardsContainer = styled(Box)(() => ({
  position: "sticky",
  top: 0,
  backgroundColor: "#ffffff",
  zIndex: 100,
  paddingTop: "10px",
  paddingBottom: "10px",
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
}));

const ScrollableContent = styled(Box)(() => ({
  overflowY: "auto",
  maxHeight: "calc(100vh - 150px)",
}));

const StyledTableContainer = styled(TableContainer)(() => ({
  borderRadius: "16px",
  boxShadow: "0px 6px 15px rgba(0,0,0,0.12)",
  border: "1px solid #d6dce5",
  backgroundColor: "#ffffff",
  maxHeight: "700px",
  overflowY: "auto",
  overflowX: "auto",
}));

const StyledTableCell = styled(TableCell)(() => ({
  fontFamily: "'Poppins', sans-serif",
  padding: "14px 10px",
  fontSize: "14px",
  textAlign: "center",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  "&.MuiTableCell-head": {
    backgroundColor: "#154C79",
    color: "#FFFFFF",
    fontWeight: 600,
    position: "sticky",
    top: 0,
    zIndex: 10,
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  transition: "0.25s ease",
  "&:nth-of-type(odd)": { backgroundColor: "rgba(243, 246, 250, 0.6)" },
  "&:hover": { backgroundColor: "rgba(14, 60, 102, 0.09)" },
}));

export default function EmpDiscripency() {
  const dispatch = useDispatch();
  const { loading, data } = useSelector((state) => state.empDiscripency);

  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());

  const formatDate = (dateObj) => {
    const d = new Date(dateObj);
    return d.toISOString().split("T")[0];
  };

  const formatTime = (dateObj) => {
    const d = new Date(dateObj);
    return d.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  useEffect(() => {
    dispatch(fetchEmpDiscripency(formatDate(selectedDate)));
  }, [dispatch, selectedDate]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setOpenDatePicker(false);
  };

  const totalEmployees = data?.length || 0;

  // ================= PDF EXPORT =================
  const handleExportPDF = () => {
    const doc = new jsPDF("p", "mm", "a4");
    const generatedAt = new Date();
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.addImage(homeImage, "PNG", 0, 0, 210, 65);

    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(21, 76, 121);
    doc.text("Employee Allocation Discrepancy Report", pageWidth / 2, 75, { align: "center" });

    doc.setDrawColor(21, 76, 121);
    doc.setLineWidth(0.5);
    doc.line(15, 82, pageWidth - 15, 82);

    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Date - ", 16, 88);
    doc.setFont("helvetica", "normal");
    doc.text(formatDate(selectedDate), 28, 88);

    doc.setFont("helvetica", "bold");
    doc.text("Time - ", pageWidth / 2 - 23, 88);
    doc.setFont("helvetica", "normal");
    doc.text(formatTime(generatedAt), pageWidth / 2 - 8, 88);

    doc.setFont("helvetica", "bold");
    doc.text("Total Employees - ", pageWidth - 63, 88);
    doc.setFont("helvetica", "normal");
    doc.text(totalEmployees.toString(), pageWidth - 25, 88);

    autoTable(doc, {
      startY: 95,
      head: [["Location", "Service No", "Name", "Designation", "Mobile"]],
      body: data?.map((item) => [
        item.Emp_location || "",
        item.Emp_serviceNo || "",
        item.Emp_name || "",
        item.Emp_designation || "",
        item.Emp_mobile || "",
      ]),
      styles: { fontSize: 12, cellPadding: 5, lineColor: [220, 220, 220], lineWidth: 0.1 },
      headStyles: { fillColor: [21, 76, 121], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 10, halign: "center", cellPadding: 6 },
      alternateRowStyles: { fillColor: [248, 249, 252] },
      columnStyles: {
        0: { cellWidth: 25, halign: "center" },
        1: { cellWidth: 28, halign: "center" },
        2: { cellWidth: 50, halign: "left" },
        3: { cellWidth: 48, halign: "left" },
        4: { cellWidth: 36, halign: "center" },
      },
      margin: { left: 10, right: 10 },
      theme: "grid",
      didDrawPage: function (data) {
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.setFont("helvetica", "italic");
        doc.text(
          `Generated on ${formatDate(generatedAt)} at ${formatTime(generatedAt)}`,
          pageWidth / 2,
          doc.internal.pageSize.getHeight() - 10,
          { align: "center" }
        );
        doc.text(`Page ${data.pageNumber}`, pageWidth - 20, doc.internal.pageSize.getHeight() - 10);
      },
    });

    doc.save(`Employee_Discrepancy_${formatDate(selectedDate)}.pdf`);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <PageContainer>
        <Typography
          sx={{
            fontSize: "20px",
            fontWeight: 700,
            color: "#0e3c66",
            textAlign: "center",
            mb: 2,
          }}
        >
          Employee Discrepancy
        </Typography>

        {!loading && (
          <StickyCardsContainer>
            {/* ── Date | Total | Export PDF — single row on desktop ── */}
            <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">

              {/* Date — half width mobile, 1/3 desktop */}
              <Grid item xs={6} sm={4}>
                <Button
                  variant="contained"
                  startIcon={<CalendarTodayIcon />}
                  onClick={() => setOpenDatePicker(true)}
                  sx={{
                    width: "100%",
                    height: "56px",
                    borderRadius: "20px",
                    backgroundColor: "#142c54",
                    fontWeight: "bold",
                    textTransform: "none",
                    fontSize: { xs: "13px", sm: "16px" },
                  }}
                >
                  {formatDate(selectedDate)}
                </Button>
              </Grid>

              {/* Total — half width mobile, 1/3 desktop */}
              <Grid item xs={6} sm={4}>
                <Box
                  sx={{
                    background: "#e8f0fa",
                    borderRadius: "20px",
                    height: "56px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: { xs: "13px", sm: "15px" },
                    color: "#142c54",
                  }}
                >
                  Total: {totalEmployees}
                </Box>
              </Grid>

              {/* Export PDF — full width mobile, 1/3 desktop */}
              <Grid item xs={12} sm={4}>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportPDF}
                  disabled={loading || !data?.length}
                  sx={{
                    width: "100%",
                    height: "56px",
                    borderRadius: "20px",
                    backgroundColor: "#2e7d32",
                    color: "#fff",
                    fontWeight: "bold",
                    textTransform: "none",
                    fontSize: { xs: "13px", sm: "16px" },
                    "&:hover": { backgroundColor: "#1b5e20" },
                    "&:disabled": { backgroundColor: "#cccccc", color: "#666666" },
                  }}
                >
                  Export PDF
                </Button>
              </Grid>
            </Grid>

            {openDatePicker && (
              <DatePicker
                open={openDatePicker}
                value={selectedDate}
                onChange={handleDateChange}
                onClose={() => setOpenDatePicker(false)}
                renderInput={(params) => (
                  <TextField {...params} sx={{ display: "none" }} />
                )}
              />
            )}
          </StickyCardsContainer>
        )}

        <ScrollableContent>
          <StyledTableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <StyledTableCell sx={{ textAlign: "center" }}>Location</StyledTableCell>
                  <StyledTableCell sx={{ textAlign: "center" }}>Service No</StyledTableCell>
                  <StyledTableCell sx={{ textAlign: "center" }}>Name</StyledTableCell>
                  <StyledTableCell sx={{ textAlign: "center" }}>Designation</StyledTableCell>
                  <StyledTableCell sx={{ textAlign: "center" }}>Mobile</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && data?.length > 0 ? (
                  data.map((item, index) => (
                    <StyledTableRow key={index}>
                      <StyledTableCell sx={{ textAlign: "center" }}>{item.Emp_location}</StyledTableCell>
                      <StyledTableCell sx={{ textAlign: "center" }}>{item.Emp_serviceNo}</StyledTableCell>
                      <StyledTableCell sx={{ textAlign: "left" }}>{item.Emp_name}</StyledTableCell>
                      <StyledTableCell sx={{ textAlign: "left" }}>{item.Emp_designation}</StyledTableCell>
                      <StyledTableCell sx={{ textAlign: "center" }}>{item.Emp_mobile}</StyledTableCell>
                    </StyledTableRow>
                  ))
                ) : (
                  <TableRow>
                    <StyledTableCell colSpan={5}>
                      {loading ? "Loading..." : "No Records Found"}
                    </StyledTableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </StyledTableContainer>
        </ScrollableContent>
      </PageContainer>
    </LocalizationProvider>
  );
}